import { useEffect, useState } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, SlidersHorizontal } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { registrarMovimiento } from '../lib/cola';
import { leerConCopia } from '../lib/conCopia';
import { useAuth } from '../lib/contextoAuth';
import NotaGuardadoEnTelefono from '../integrador/pwa/NotaGuardadoEnTelefono';
import { lineaDePresentacion } from '../accesos/codigos';
import Ayuda from '../ui/Ayuda';
import { useFormularioTocado } from '../ui/navegacionReact';
import {
  leerUltimaUbicacion, guardarUltimaUbicacion, sugerirDestino,
} from '../lib/ubicacionSugerida';

const TIPOS = [
  { id: 'ENTRADA', label: 'Entrada', icono: ArrowDownToLine, color: '#16a34a' },
  { id: 'SALIDA', label: 'Salida', icono: ArrowUpFromLine, color: '#dc2626' },
  { id: 'TRASPASO', label: 'Traspaso', icono: ArrowLeftRight, color: 'var(--color-marca)' },
  { id: 'AJUSTE', label: 'Ajuste', icono: SlidersHorizontal, color: '#9333ea' },
];

// Que ubicaciones pide cada tipo. El ajuste se resuelve aparte: su
// ubicación depende de si sube o baja el stock.
const USA_ORIGEN = { SALIDA: true, TRASPASO: true };
const USA_DESTINO = { ENTRADA: true, TRASPASO: true };

// Por qué se ajusta el inventario. Lista cerrada, espejo del backend
// (schemas.MOTIVOS_AJUSTE): "ajuste de 40 piezas, motivo: corrección" no le
// sirve a nadie que después tenga que explicar dónde quedaron.
const MOTIVOS = [
  { id: 'ERROR_CONTEO', label: 'Error de conteo' },
  { id: 'MERMA', label: 'Merma' },
  { id: 'DANO', label: 'Daño' },
  { id: 'ROBO_DETECTADO', label: 'Robo detectado' },
  { id: 'CADUCIDAD', label: 'Caducidad' },
  { id: 'DEVOLUCION', label: 'Devolución' },
];

// `presentacion` viene de la resolucion del codigo escaneado y hasta ahora
// se tiraba: la pantalla recibia el producto y nada mas.
//
// EL FALLO. El backend ya resolvia el codigo de barras de la CAJA y decia
// cuantas unidades trae —lo dice su propio comentario: «la caja de doce
// tiene que meter doce, y la pantalla no puede adivinarlo»—, pero aqui,
// que es LA pantalla donde se escanea, nadie leia ese dato. Quien escanea
// una caja de doce escribia 1, o escribia 12 de memoria y a veces 21.
// `tipoInicial` (P17, opcional): el acceso del inicio del teléfono
// —«Entrada», «Salida», «Traspaso»— llega con el tipo ya elegido. Sin él,
// como siempre, arranca en Entrada. El ajuste nunca llega preelegido: es
// de encargado y se elige a propósito.
const TIPOS_DE_ACCESO = new Set(['ENTRADA', 'SALIDA', 'TRASPASO']);

// U-02 fase b · `codigoEscaneado` y `medio` (opcionales) vienen de Escanear:
// el código que el servidor resolvió y por dónde se leyó (LECTOR, CAMARA,
// TECLADO o URL). Viajan en el cuerpo como `codigo_escaneado` y
// `medio_captura`, también en la cola sin señal (`lib/cola.js` guarda el
// movimiento entero). Sin ellos, el movimiento va como antes: elegido de
// una lista no tiene código que apuntar.
export default function MovimientoForm({ producto, presentacion, onListo, tipoInicial, codigoEscaneado = null, medio = null }) {
  const { tieneNivel, usuario, puede } = useAuth();
  const [tipo, setTipo] = useState(TIPOS_DE_ACCESO.has(tipoInicial) ? tipoInicial : 'ENTRADA');
  // La cantidad arranca con lo que trae la presentacion escaneada. Es un
  // punto de partida, no una imposicion: se puede cambiar.
  const [cantidad, setCantidad] = useState(
    presentacion?.unidades ? String(Number(presentacion.unidades)) : '',
  );
  const [origen, setOrigen] = useState('');
  const [destino, setDestino] = useState('');
  const [sentidoAjuste, setSentidoAjuste] = useState('BAJA');
  const [motivoTipo, setMotivoTipo] = useState('ERROR_CONTEO');
  const [motivo, setMotivo] = useState('');
  const [lote, setLote] = useState('');
  const [sugerencia, setSugerencia] = useState(null);
  const [ubicaciones, setUbicaciones] = useState([]);
  // DIRECTRIZ-PWA F2 (DISENO-F2 §4.5; parche del integrador para
  // experiencia): la hora de la copia si las ubicaciones salieron del
  // teléfono. Null = vinieron de la API.
  const [copiaDe, setCopiaDe] = useState(null);
  const [saldos, setSaldos] = useState(null); // {codigo: saldo} de este producto, o null
  const [control, setControl] = useState(null);
  const [msg, setMsg] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  // MOVIMIENTO A MEDIAS · TC-DR-AT. Si se tocó la cantidad, la ubicación o
  // el motivo y no se ha registrado, atrás pregunta antes de dejarlo. Lo
  // ya registrado (o guardado en el teléfono sin señal) no cuenta.
  const [vigilar, limpiarGuardia] = useFormularioTocado({ sub: 'resultado' });
  // P15 · la última ubicación a la que esta persona metió o movió algo.
  const [ultima, setUltima] = useState(() => leerUltimaUbicacion(usuario?.id));

  // Los dos catálogos con copia (DISENO-F2 §4.5): sin señal el formulario
  // ya no llega sin ubicaciones, y el interruptor de control viene de la
  // última respuesta buena, no de un valor por omisión (la última verdad
  // conocida gana al default, como en colaRecepciones).
  useEffect(() => {
    leerConCopia(usuario?.id, '/ubicaciones')
      .then(({ datos, momento }) => { setUbicaciones(datos); setCopiaDe(momento); })
      .catch(() => setUbicaciones([]));
    leerConCopia(usuario?.id, '/configuracion/control')
      .then(({ datos }) => setControl(datos))
      .catch(() => setControl(null));
  }, [usuario?.id]);

  // T-11 · El "Desde" de una SALIDA o un TRASPASO tiene que ofrecer dónde
  // HAY este producto, no la primera ubicación del abecedario. Se consulta
  // la existencia por ubicación, la de mayor saldo queda arriba y
  // preseleccionada. Un fallo de red NO estorba: `saldos` queda en null y
  // el selector vuelve a la lista completa, como antes.
  useEffect(() => {
    if (!producto?.sku) { setSaldos(null); return undefined; }
    let vivo = true;
    api.get(`/inventario/existencias?sku=${encodeURIComponent(producto.sku)}`
            + '&solo_con_stock=true&limite=1000')
      .then((filas) => {
        if (!vivo) return;
        const por = {};
        for (const f of (filas || [])) {
          por[f.ubicacion] = (por[f.ubicacion] || 0) + Number(f.cantidad || 0);
        }
        setSaldos(por);
        // La de mayor saldo queda propuesta, salvo que ya se haya elegido
        // una a mano (`prev` no vacío).
        const mejor = Object.entries(por).sort((a, b) => b[1] - a[1])[0];
        if (mejor && mejor[1] > 0) setOrigen((prev) => prev || mejor[0]);
      })
      .catch(() => { if (vivo) setSaldos(null); });
    return () => { vivo = false; };
  }, [producto?.sku]);

  // AJUSTE y MERMA exigen ENCARGADO; ENTRADA/SALIDA/TRASPASO los hace un operador.
  const puedeAjuste = tieneNivel('ENCARGADO');
  const esAjuste = tipo === 'AJUSTE';

  // ¿Esto va a caer en aprobación? Se avisa ANTES de pulsar, no después:
  // un operador que descubre el control cuando ya creía haber despachado
  // pierde la confianza en la pantalla.
  //
  // SEG-4b (1 oct 2026) · EL AVISO POR VALOR, SOLO CON EL PERMISO `costo`.
  // Sin el permiso, `costo_promedio` llega en null y la cuenta daba cero:
  // el aviso por valor callaba y la salida caía en aprobación por sorpresa.
  // Se descartó preguntarle al servidor «¿esta cantidad cae?»: un sí/no por
  // cantidad deja acotar costo × cantidad ≥ umbral probando cantidades, y
  // con eso se despeja el costo. Quien no ve el costo recibe, en cambio, un
  // aviso FIJO que no depende de la cantidad —no hay nada que despejar—, y
  // el aviso por CANTIDAD sigue igual para todos (no dice nada del costo).
  const veCosto = Boolean(puede?.('costo'));
  const valor = Number(cantidad || 0) * Number(producto.costo_promedio || 0);
  const saleDelAlmacen = tipo === 'SALIDA' || (esAjuste && sentidoAjuste === 'BAJA');
  const vigilaSalida = Boolean(control?.control_activo && saleDelAlmacen);
  const caePorValor = vigilaSalida && veCosto && Number(cantidad) > 0 &&
    control.umbral_valor != null && valor >= Number(control.umbral_valor);
  const caePorCantidad = vigilaSalida && Number(cantidad) > 0 &&
    control.umbral_cantidad != null && Number(cantidad) >= Number(control.umbral_cantidad);
  const caeEnAprobacion = caePorValor || caePorCantidad;
  const avisoFijoPorValor = vigilaSalida && !veCosto && !caePorCantidad &&
    control.umbral_valor != null;

  // P15 · EL «HACIA» NACE PROPUESTO. Mientras la persona no elija otro, el
  // selector enseña (y se envía) la ubicación sugerida: donde ya hay de
  // este producto, o la última que usó (`lib/ubicacionSugerida.js`). Se
  // deriva en cada pintado en vez de copiarse a `destino`, así cambiar de
  // Entrada a Traspaso recalcula la propuesta sin pisar lo elegido a mano.
  const destinoSugerido = USA_DESTINO[tipo]
    ? sugerirDestino({ tipo, saldos, origen, ultima, lista: ubicaciones })
    : '';
  const destinoFinal = USA_DESTINO[tipo] ? (destino || destinoSugerido) : destino;

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setMsg(null);
    setAviso(null);
    const cant = Number(cantidad);
    if (!cant || cant <= 0) { setError('Indica una cantidad mayor a cero.'); return; }

    const mov = { tipo, sku: producto.sku, cantidad: cant };
    if (codigoEscaneado) mov.codigo_escaneado = codigoEscaneado;
    if (medio) mov.medio_captura = medio;
    if (USA_ORIGEN[tipo]) mov.ubicacion_origen = origen;
    if (USA_DESTINO[tipo]) mov.ubicacion_destino = destinoFinal;
    if (producto.controla_lote) {
      if (!lote.trim()) { setError('Este producto exige indicar el lote.'); return; }
      mov.codigo_lote = lote.trim().toUpperCase();
    }
    if (esAjuste) {
      // El ajuste lleva UNA ubicación: destino para subir, origen para bajar.
      if (sentidoAjuste === 'ALTA') mov.ubicacion_destino = destino;
      else mov.ubicacion_origen = destino;
      mov.motivo_tipo = motivoTipo;
      if (motivo.trim()) mov.motivo = motivo.trim();
    }

    setEnviando(true);
    try {
      const r = await registrarMovimiento(mov);
      if (r.estado === 'aplicado') {
        setMsg(r.movimiento?.desvio_politica
          ? `Movimiento aplicado. Ojo: ${r.movimiento.desvio_politica}.`
          : 'Movimiento aplicado.');
      } else if (r.estado === 'pendiente') {
        setAviso(r.solicitud.mensaje);
      } else {
        setMsg('Sin conexión: guardado en el teléfono. Se enviará solo al reconectar.');
      }
      if (USA_DESTINO[tipo] && destinoFinal) {
        guardarUltimaUbicacion(usuario?.id, destinoFinal);
        setUltima(destinoFinal);
      }
      setCantidad('');
      limpiarGuardia();
      setLote('');
      setSugerencia(null);
      // Una solicitud pendiente no se cierra sola: el operador tiene que
      // leer que su movimiento NO se aplicó.
      if (onListo && r.estado !== 'pendiente') setTimeout(onListo, 900);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo registrar.');
    } finally {
      setEnviando(false);
    }
  }

  // Qué lote toca sacar. Se pregunta cuando ya hay de dónde y cuánto: sin
  // esos dos datos no hay nada que sugerir. La política vive en el
  // producto; si es NINGUNA el backend devuelve una lista vacía y aquí no
  // se pinta nada.
  const ubicSalida = esAjuste && sentidoAjuste === 'BAJA' ? destino : origen;
  useEffect(() => {
    const cant = Number(cantidad);
    if (!producto.controla_lote || !saleDelAlmacen || !ubicSalida || !cant) {
      const t = setTimeout(() => setSugerencia(null), 0);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      api.get(`/inventario/sugerencia-lote?sku=${encodeURIComponent(producto.sku)}`
              + `&ubicacion=${encodeURIComponent(ubicSalida)}&cantidad=${cant}`)
        .then((d) => setSugerencia(d.lotes.length ? d : null))
        .catch(() => setSugerencia(null));
    }, 250);
    return () => clearTimeout(t);
  }, [producto.sku, producto.controla_lote, saleDelAlmacen, ubicSalida, cantidad]);

  const tiposVisibles = TIPOS.filter((t) => t.id !== 'AJUSTE' || puedeAjuste);

  return (
    <form ref={vigilar} onSubmit={enviar} className="space-y-4">
      <div className="grid grid-cols-4 gap-2">
        {tiposVisibles.map((t) => {
          const activo = tipo === t.id;
          const Icono = t.icono;
          return (
            <button
              type="button"
              key={t.id}
              onClick={() => setTipo(t.id)}
              className={`flex flex-col items-center gap-1 py-3 rounded-xl border text-xs font-semibold transition-colors ${
                activo ? 'text-white' : 'bg-white text-gray-600 border-gray-200'
              }`}
              style={activo ? { background: t.color, borderColor: t.color } : {}}
            >
              <Icono className="h-6 w-6" />
              {t.label}
            </button>
          );
        })}
      </div>

      <label className="block">
        <span className="text-sm font-semibold text-gray-700">Cantidad ({producto.unidad_medida})</span>
        <input
          type="number"
          inputMode="decimal"
          step="any"
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
          autoFocus
          className="entrada text-2xl text-center mt-1"
          placeholder="0"
        />
        {/* DE DONDE SALE ESE NÚMERO. Un campo que aparece relleno sin
            explicación se borra por desconfianza —y con razón—: quien
            escanea tiene que poder ver que son 12 porque el código era el
            de la caja, no porque el sistema se lo haya inventado. */}
        {/* U-02 fase c: con el nivel, «Caja: 12 piezas por escaneo.»; sin
            él, el código por su descripción o por su tipo con nombre
            humano (antes salía «Código de itf14»). */}
        {presentacion?.unidades ? (
          <span className="block text-xs text-[var(--color-marca)] font-semibold mt-1">
            {lineaDePresentacion(presentacion, producto.unidad_medida)}
            {' '}Cámbialo si no es lo que tienes delante.
          </span>
        ) : null}
      </label>

      {USA_ORIGEN[tipo] && (
        <SelectUbic label="Desde" valor={origen} set={setOrigen} lista={ubicaciones} saldos={saldos} />
      )}
      {USA_DESTINO[tipo] && (
        <div>
          <SelectUbic label="Hacia" valor={destinoFinal} set={setDestino} lista={ubicaciones} />
          {!destino && destinoSugerido && <Ayuda id="movimiento.hacia_sugerida" />}
        </div>
      )}
      {!esAjuste && <NotaGuardadoEnTelefono momento={copiaDe} que="la lista de ubicaciones" />}

      {esAjuste && (
        <>
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: 'BAJA', label: 'Bajar stock' },
              { id: 'ALTA', label: 'Subir stock' },
            ].map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => setSentidoAjuste(s.id)}
                className={`py-2.5 rounded-xl border text-sm font-semibold ${
                  sentidoAjuste === s.id
                    ? 'bg-[#9333ea] text-white border-[#9333ea]'
                    : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <SelectUbic label="Ubicación" valor={destino} set={setDestino} lista={ubicaciones} />
          <NotaGuardadoEnTelefono momento={copiaDe} que="la lista de ubicaciones" />
          <label className="block">
            <span className="text-sm font-semibold text-gray-700">¿Por qué se ajusta?</span>
            <select value={motivoTipo} onChange={(e) => setMotivoTipo(e.target.value)} className="entrada mt-1">
              {MOTIVOS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700">Detalle (opcional)</span>
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej.: caja mojada en el pasillo 3"
              className="entrada mt-1"
            />
          </label>
        </>
      )}

      {producto.controla_lote && (
        <label className="block">
          <span className="block text-sm font-semibold text-gray-700 mb-1">Lote</span>
          <input
            value={lote}
            onChange={(e) => setLote(e.target.value)}
            placeholder="Código del lote"
            className="entrada font-mono"
            autoCapitalize="characters"
          />
        </label>
      )}

      {/* Qué lote toca sacar, y POR QUÉ. Una sugerencia sin motivo se
          ignora; con motivo, se discute — que ya es otra cosa. */}
      {sugerencia && (
        <div className="bg-[var(--color-marca-suave)] border border-[var(--color-marca-borde)] rounded-xl p-3 space-y-2">
          <div className="text-sm font-semibold text-[var(--color-marca-oscura)]">
            {sugerencia.politica === 'FEFO'
              ? 'Sale primero lo que caduca antes'
              : 'Sale primero lo que entró antes'}
          </div>
          <p className="text-sm text-[var(--color-marca-oscura)] leading-relaxed">
            {sugerencia.explicacion}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {sugerencia.lotes.map((l) => (
              <button
                type="button"
                key={l.codigo_lote}
                onClick={() => setLote(l.codigo_lote)}
                className={`text-xs font-mono font-semibold px-2 py-1 rounded border ${
                  l.caducado
                    ? 'bg-red-50 border-red-300 text-red-800'
                    : lote.toUpperCase() === l.codigo_lote
                      ? 'bg-[var(--color-marca)] border-[var(--color-marca)] text-white'
                      : 'bg-white border-[var(--color-marca-borde)] text-[var(--color-marca-oscura)]'
                }`}
              >
                {l.codigo_lote} · {Number(l.cantidad)}
                {l.dias_para_caducar != null && (
                  <span className="ml-1 font-sans font-normal">
                    ({l.caducado ? 'caducado' : `${l.dias_para_caducar} d`})
                  </span>
                )}
              </button>
            ))}
          </div>
          {lote && !sugerencia.lotes.some((l) => l.codigo_lote === lote.toUpperCase()) && (
            <p className="text-xs text-amber-800">
              Vas a sacar un lote distinto del sugerido. Queda registrado en el
              movimiento.
            </p>
          )}
        </div>
      )}

      {caeEnAprobacion && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2.5">
          {caePorValor ? 'Por su valor' : 'Por la cantidad'}, este movimiento necesitará el visto bueno de otra persona.
          Al confirmarlo quedará en espera y <strong>el stock no cambiará todavía</strong>.
        </div>
      )}
      {avisoFijoPorValor && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2.5">
          Algunas salidas esperan la firma de otra persona antes de hacerse. Si esta es una
          de ellas, al confirmarla verás que quedó en espera y el stock no cambiará hasta la firma.
        </div>
      )}

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">{error}</div>}
      {msg && <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded p-2.5">{msg}</div>}
      {aviso && (
        <div className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded p-2.5">{aviso}</div>
      )}

      <button
        type="submit"
        disabled={enviando}
        className="w-full bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] disabled:opacity-60 text-white font-bold py-4 rounded-xl text-base"
      >
        {enviando ? 'Registrando…' : 'Confirmar movimiento'}
      </button>
    </form>
  );
}

// `saldos` es opcional: cuando llega (solo en el "Desde"), el selector
// separa las ubicaciones con existencia de este producto —ordenadas por
// saldo, la mayor arriba— de las que no tienen. Sin `saldos` (destino,
// ajuste, o si falló la consulta) enseña la lista completa como siempre.
function SelectUbic({ label, valor, set, lista, saldos }) {
  const con = saldos
    ? lista.filter((u) => Number(saldos[u.codigo]) > 0)
        .sort((a, b) => Number(saldos[b.codigo]) - Number(saldos[a.codigo]))
    : [];
  const sin = saldos ? lista.filter((u) => !(Number(saldos[u.codigo]) > 0)) : lista;
  return (
    <label className="block">
      <span className="text-sm font-semibold text-gray-700">{label}</span>
      <select value={valor} onChange={(e) => set(e.target.value)} className="entrada mt-1">
        <option value="">Selecciona ubicación…</option>
        {saldos && con.length > 0 ? (
          <>
            <optgroup label="Con existencia de este producto">
              {con.map((u) => (
                <option key={u.codigo} value={u.codigo}>
                  {u.codigo} — {u.nombre} · {saldos[u.codigo]} disponibles
                </option>
              ))}
            </optgroup>
            {sin.length > 0 && (
              <optgroup label="Otras ubicaciones (sin existencia)">
                {sin.map((u) => (
                  <option key={u.codigo} value={u.codigo}>{u.codigo} — {u.nombre}</option>
                ))}
              </optgroup>
            )}
          </>
        ) : (
          lista.map((u) => (
            <option key={u.codigo} value={u.codigo}>{u.codigo} — {u.nombre}</option>
          ))
        )}
      </select>
      {saldos && con.length === 0 && (
        <p className="text-xs text-amber-700 mt-1">
          Ningún sitio tiene existencia de este producto. Recíbelo antes de
          sacarlo o traspasarlo.
        </p>
      )}
    </label>
  );
}
